import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MagnifyingGlass, DownloadSimple } from "@phosphor-icons/react";
import { NETWORK_FILTER_OPTIONS } from "./constants";

/**
 * Catalog search + network filter chips + Excel export button.
 *
 * The export hits /admin/brands/export.xlsx server-side and streams
 * one sheet per network plus an "All Offers" tab. We pass the
 * currently-active network filter through as `?network=…` so admins
 * can export just one slice when needed.
 */
export default function BrandsFilterBar({
  searchInput, onSearchInputChange,
  networkFilter, onNetworkFilterChange,
  hasTraffic30d, onHasTraffic30dChange,
  hideInactive, onHideInactiveChange,
  totalCount,
  exporting, onExport,
}) {
  const networkLabel = NETWORK_FILTER_OPTIONS.find((o) => o.k === networkFilter)?.label;
  const exportLabel = networkFilter === "all"
    ? "Export All"
    : `Export ${networkLabel || networkFilter}`;
  const exportTitle = networkFilter === "all"
    ? "Download the full catalog as Excel — one sheet per network plus an 'All Offers' tab"
    : `Download only ${networkLabel || networkFilter} brands as Excel`;

  return (
    <div className="mt-6 mb-4 space-y-2">
      <div className="flex items-center gap-2">
        <div className="relative flex-1 min-w-0">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder="Search catalog by name, advertiser, or campaign ID…"
            value={searchInput}
            onChange={(e) => onSearchInputChange(e.target.value)}
            className="pl-9 h-10"
            data-testid="brands-search-input"
          />
        </div>
        <button
          type="button"
          onClick={() => onHideInactiveChange(!hideInactive)}
          title="Hide brands marked inactive (removed from upstream feeds like mrge or FlexOffers). Turn off to audit ghost catalog rows."
          className={`h-10 shrink-0 px-3 text-xs font-semibold uppercase tracking-wider rounded-md border transition-colors whitespace-nowrap ${
            hideInactive
              ? "bg-slate-800 text-white border-slate-800 hover:bg-slate-900"
              : "bg-white text-slate-500 border-slate-200 hover:bg-slate-50"
          }`}
          data-testid="brands-filter-hide-inactive"
        >
          {hideInactive ? "● Hide inactive" : "Hide inactive"}
        </button>
        <button
          type="button"
          onClick={() => onHasTraffic30dChange(!hasTraffic30d)}
          title="Show only brands with at least one click in the last 30 days — the active catalog, recommended starting point for filling in traffic rules."
          className={`h-10 shrink-0 px-3 text-xs font-semibold uppercase tracking-wider rounded-md border transition-colors whitespace-nowrap ${
            hasTraffic30d
              ? "bg-emerald-600 text-white border-emerald-600 hover:bg-emerald-700"
              : "bg-white text-slate-500 border-slate-200 hover:bg-slate-50"
          }`}
          data-testid="brands-filter-has-traffic-30d"
        >
          {hasTraffic30d ? "● Active (30d)" : "Active (30d)"}
        </button>
        <Button
          variant="outline"
          size="sm"
          className="h-10 shrink-0 border-emerald-200 text-emerald-700 hover:bg-emerald-50 px-3 whitespace-nowrap"
          disabled={exporting || totalCount === 0}
          onClick={onExport}
          title={exportTitle}
          data-testid="export-brands-xlsx-btn"
        >
          <DownloadSimple size={14} className={`mr-1.5 ${exporting ? "animate-pulse" : ""}`} />
          {exporting ? "Exporting…" : exportLabel}
        </Button>
      </div>
      <div className="overflow-x-auto -mx-1 px-1 pb-0.5" data-testid="network-filter">
        <div className="inline-flex items-center border border-slate-200 rounded-md overflow-hidden h-10 min-w-max">
          {NETWORK_FILTER_OPTIONS.map((opt) => (
            <button
              key={opt.k}
              onClick={() => onNetworkFilterChange(opt.k)}
              className={`px-3 h-full text-xs font-semibold uppercase tracking-wider transition-colors whitespace-nowrap ${
                networkFilter === opt.k ? "bg-slate-900 text-white" : "bg-white text-slate-500 hover:bg-slate-50"
              }`}
              data-testid={`network-filter-${opt.k}`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
